/**
 * Persistence for compass entries: the localStorage-backed working set.
 *
 * Loading never throws: a missing, corrupt or hand-edited store behaves like an
 * empty list. There is no file import or export any more — sharing is the URL
 * (`share.ts`), so validation of untrusted input lives in `decodeShare`. What
 * remains here is private: it keeps existing stores readable and drops
 * individual entries with invalid coordinates or years rather than failing the
 * whole load. A loaded coordinate is rounded to the two decimals the UI displays
 * and the share codec carries, so the store cannot hold a value the chart, a row
 * input and a shared link would disagree about.
 */

import { isValidCoord, isValidYear, roundCoord } from './compass'
import { MAX_DESCRIPTION_LENGTH } from './types'
import type { Entry } from './types'

/** localStorage key holding the working set. Versioned to allow migrations. */
export const STORAGE_KEY = 'polcomp-trajectory:v1'

/** Re-exported so callers can cap input at the same bound the parser enforces. */
export { MAX_DESCRIPTION_LENGTH }

/**
 * Mint a fresh entry id, preferring the platform UUID generator.
 *
 * Exported so every entry-producing path (a new row, a stored payload, a shared
 * link) mints ids the same way without constructing an `Entry` just to reach
 * one, and so a caller can mint ids unique within its own set.
 */
export function createEntryId(): string {
  const platformCrypto = globalThis.crypto
  if (platformCrypto && typeof platformCrypto.randomUUID === 'function') {
    return platformCrypto.randomUUID()
  }
  return `entry-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/** True for a plain (non-array, non-null) object. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Create an entry, filling missing fields with safe defaults: a fresh id,
 * `x`/`y` of 0, the current year and an empty description.
 */
export function createEntry(partial: Partial<Entry> = {}): Entry {
  return {
    id: partial.id ?? createEntryId(),
    x: partial.x ?? 0,
    y: partial.y ?? 0,
    year: partial.year ?? new Date().getFullYear(),
    description: partial.description ?? '',
  }
}

/**
 * Read the saved entries.
 *
 * Returns `[]` when nothing has been saved or when the stored value is corrupt
 * (bad JSON, wrong shape); entries with invalid coordinates or years are
 * dropped rather than failing the whole load.
 *
 * The read itself is inside the guard on purpose. Storage access is not
 * guaranteed — Safari with cookies or storage blocked, some in-app webviews and
 * a restricted iframe all throw on `localStorage` (or on `getItem`) rather than
 * returning `null`. This runs during render, from App's `useState`
 * initializer, and an exception there unmounts the whole tree: the user gets a
 * blank page with no UI at all, not even the address bar that still holds their
 * data. A store that cannot be read is an empty history instead.
 */
export function loadEntries(): Entry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return []
    return parseStored(raw)
  } catch {
    return []
  }
}

/**
 * Persist entries as JSON. Storage quota / privacy-mode failures are ignored so
 * that saving can never break the UI; the in-memory state remains the source of
 * truth.
 *
 * The `{ entries }` envelope is kept so a store written by an earlier version
 * (which also carried a `version` field) still has the same shape; nothing but
 * this module reads the raw value.
 */
export function saveEntries(entries: Entry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ entries }))
  } catch {
    // Ignore quota / serialization failures: persistence is best-effort.
  }
}

/**
 * Validate and normalise a stored value into entries.
 *
 * Accepts the `{ entries }` envelope this module writes, an older
 * `{ version, entries }` one, or a bare array (hand-edited data); anything else
 * reads as empty. Individual entries with invalid `x`/`y`/`year` are dropped so
 * one bad row cannot poison the store; ids are made unique within the store and
 * descriptions are capped at `MAX_DESCRIPTION_LENGTH`.
 *
 * Private on purpose: `loadEntries` is the only supported way in, and the
 * behaviour is covered through it.
 */
function parseStored(text: string): Entry[] {
  const parsed: unknown = JSON.parse(text)

  let rawList: unknown[]
  if (Array.isArray(parsed)) {
    rawList = parsed
  } else if (isRecord(parsed) && Array.isArray(parsed.entries)) {
    rawList = parsed.entries
  } else {
    return []
  }

  const entries: Entry[] = []
  const seenIds = new Set<string>()
  for (const item of rawList) {
    const entry = toEntry(item, seenIds)
    if (entry !== null) entries.push(entry)
  }
  return entries
}

/**
 * Claim a unique id for a stored entry.
 *
 * A well-formed id that has not yet been seen in this store is kept verbatim.
 * A missing, non-string or already-used id gets a freshly generated one, so two
 * entries can never share an id (which would make React keys and edit/delete
 * actions target only the first match).
 */
function claimId(id: unknown, seenIds: Set<string>): string {
  if (typeof id === 'string' && id !== '' && !seenIds.has(id)) {
    seenIds.add(id)
    return id
  }

  let fresh = createEntryId()
  while (seenIds.has(fresh)) fresh = createEntryId()
  seenIds.add(fresh)
  return fresh
}

/**
 * Validate and normalise a single raw value into an Entry, or `null` if unusable.
 *
 * Coordinates are rounded to the hundredth the app displays and shares (see
 * {@link roundCoord}, the single rounding implementation in `compass.ts`), so a
 * legacy or hand-edited store cannot smuggle in more precision than a row input
 * or a share link would ever produce.
 */
function toEntry(value: unknown, seenIds: Set<string>): Entry | null {
  if (!isRecord(value)) return null

  const { id, x, y, year, description } = value
  if (!isValidCoord(x) || !isValidCoord(y) || !isValidYear(year)) return null

  const entry: Entry = { id: claimId(id, seenIds), x: roundCoord(x), y: roundCoord(y), year }
  if (typeof description === 'string') {
    // Slice by code point: `String.prototype.slice` cuts UTF-16 units, which
    // would split an emoji at the boundary into a lone surrogate.
    const codePoints = Array.from(description)
    entry.description =
      codePoints.length > MAX_DESCRIPTION_LENGTH
        ? codePoints.slice(0, MAX_DESCRIPTION_LENGTH).join('')
        : description
  }
  return entry
}
